import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './KenoRoom.css';
import { FreePlayNote, playable } from '../../components/FreePlayNote';

/**
 * Keno Room — hub page that lists every Keno variant. Every future
 * variant lands here as an additional card. `available: true` means the
 * player can jump in; `false` renders the card locked with a "Coming
 * soon" ribbon.
 *
 * Adding a new variant later is a one-line append to `VARIANTS` and a
 * matching <Route> in App.js — no other wiring required.
 */
const VARIANTS = [
    {
        key: 'keno-4-hearts',
        title: '4 HEARTS FIRE BALL KENO',
        tagline: 'Land all 4 hearts to win 10 free plays',
        stats: '2-10 spots · $0.25 – $5.00 · Fire Ball ×4',
        cta: 'PLAY NOW',
        available: true,
        path: '/casino/keno-4-hearts',
        pill: 'NEW',
        gradient: 'radial-gradient(ellipse at 30% 20%, rgba(255,215,0,0.3) 0%, rgba(120,20,20,0.9) 40%, rgba(30,10,80,0.95) 75%, rgba(0,0,0,0.92) 100%)',
        accent: '#FFD700',
        heartsPreview: 4,
    },
    {
        key: 'super-double-up-keno',
        title: 'SUPER DOUBLE-UP KENO',
        tagline: 'Double up after 10 balls · Superball hit = 4×',
        stats: '2-10 spots · $0.25 – $5.00 · Double + Superball = 5 Free Plays',
        cta: 'PLAY NOW',
        available: true,
        hidden: false,  // Re-enabled 2026-08-14 at tuned 85% RTP with payout caps
        path: '/casino/keno-super-double',
        pill: 'NEW',
        gradient: 'radial-gradient(ellipse at 30% 20%, rgba(64,200,255,0.32) 0%, rgba(0,60,140,0.9) 40%, rgba(0,20,60,0.95) 75%, rgba(0,0,0,0.92) 100%)',
        accent: '#40c8ff',
        heartsPreview: 0,
    },
    {
        key: 'super-ball-keno',
        title: 'SUPER BALL KENO',
        tagline: '×10 multiplier on the last ball hit',
        stats: '2-10 spots · Super Ball × up to 10',
        cta: 'COMING SOON',
        available: false,
        path: null,
        pill: 'SOON',
        gradient: 'radial-gradient(ellipse at 30% 20%, rgba(64,200,255,0.28) 0%, rgba(20,60,120,0.9) 40%, rgba(10,20,60,0.95) 75%, rgba(0,0,0,0.92) 100%)',
        accent: '#40c8ff',
        heartsPreview: 0,
    },
    {
        key: 'jewel-keno',
        title: 'JEWEL KENO',
        tagline: 'Match jewels for stacking multipliers',
        stats: '2-10 spots · Jewel bonus grid',
        cta: 'COMING SOON',
        available: false,
        path: null,
        pill: 'SOON',
        gradient: 'radial-gradient(ellipse at 30% 20%, rgba(180,80,255,0.28) 0%, rgba(80,20,140,0.9) 40%, rgba(30,10,60,0.95) 75%, rgba(0,0,0,0.92) 100%)',
        accent: '#c88cff',
        heartsPreview: 0,
    },
    {
        key: 'lucky-star-keno',
        title: 'LUCKY STAR KENO',
        tagline: 'Star bombs shower extra hits mid-draw',
        stats: '2-10 spots · Random star ×2/×5/×10',
        cta: 'COMING SOON',
        available: false,
        path: null,
        pill: 'SOON',
        gradient: 'radial-gradient(ellipse at 30% 20%, rgba(80,255,140,0.28) 0%, rgba(20,80,40,0.9) 40%, rgba(10,40,20,0.95) 75%, rgba(0,0,0,0.92) 100%)',
        accent: '#39ff88',
        heartsPreview: 0,
    },
];

const KenoRoom = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    return (
        <div className="keno-room-page" data-testid="keno-room-page">
            <header className="keno-room-header">
                <button
                    className="keno-room-back"
                    onClick={() => navigate('/casino/slots')}
                    data-testid="keno-room-back"
                >← LOBBY</button>
                <div className="keno-room-title">
                    <span className="tag">BETOHALICS</span>
                    <h1>KENO HEAVEN</h1>
                    <span className="sub">Pick your Keno.</span>
                </div>
                <div className="keno-room-balance" data-testid="keno-room-balance">
                    <span className="lbl">BALANCE</span>
                    <strong>${playable(user).toFixed(2)}</strong>
                    <FreePlayNote user={user} testId="keno-room-free-play" />
                </div>
            </header>

            <main className="keno-room-grid" data-testid="keno-room-grid">
                {VARIANTS.filter(v => !v.hidden).map(v => (
                    <button
                        key={v.key}
                        type="button"
                        className={`keno-variant-tile ${v.available ? '' : 'locked'}`}
                        onClick={() => v.available && v.path && navigate(v.path)}
                        disabled={!v.available}
                        style={{ background: v.gradient, borderColor: v.accent }}
                        data-testid={`keno-variant-${v.key}`}
                    >
                        <span className={`pill ${v.pill === 'NEW' ? 'pill-new' : 'pill-soon'}`}>{v.pill}</span>
                        <div className="tile-body">
                            <div className="tile-hearts" aria-hidden="true">
                                {v.heartsPreview > 0
                                    ? Array.from({ length: v.heartsPreview }).map((_, i) => (
                                        <span key={i} className="tile-heart" />
                                    ))
                                    : <span className="tile-dot" style={{ background: v.accent }} />}
                            </div>
                            <div className="tile-title" style={{ color: v.accent }}>{v.title}</div>
                            <div className="tile-tagline">{v.tagline}</div>
                            <div className="tile-stats">{v.stats}</div>
                        </div>
                        <span className="tile-cta" style={{ background: v.accent }}>{v.cta}</span>
                    </button>
                ))}
            </main>

            <footer className="keno-room-footer">
                <span>More Keno variants launching soon.</span>
            </footer>
        </div>
    );
};

export default KenoRoom;
